import type { HomeHeroBannerItem, HomeHeroDocument } from '../types/content.types.ts'

export const HOME_HERO_SCHEMA_VERSION = 2 as const
export const DEFAULT_HOME_HERO_ROTATION_SECONDS = 8
export const MIN_HOME_HERO_ROTATION_SECONDS = 3
export const MAX_HOME_HERO_ROTATION_SECONDS = 30
export const LEGACY_HOME_HERO_BANNER_ID = 'legacy-home-hero'

export type HomeHeroConfig = Omit<HomeHeroDocument, 'updatedAt'>

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function booleanValue(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function boundedNumber(value: unknown, fallback: number, min = 0, max = 1): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.min(max, Math.max(min, value))
}

function bannerNameFromMarkdown(markdown: string): string {
  const firstLine = markdown
    .split('\n')
    .map((line) => line.trim())
    .find(Boolean)
  const withoutMarkdown = firstLine
    ?.replace(/^#{1,6}\s+/, '')
    .replace(/[*_`~[\]]/g, '')
    .trim()

  return withoutMarkdown ? withoutMarkdown.slice(0, 80) : 'Current banner'
}

export function createHomeHeroBanner(id: string, name = 'Untitled banner'): HomeHeroBannerItem {
  return {
    id,
    name,
    bodyMarkdown: '',
    ctaLabel: '',
    ctaUrl: '',
    ctaOpenInNewTab: false,
    backgroundImageUrl: '',
    backgroundMediaId: '',
    backgroundFocusX: 0.5,
    backgroundFocusY: 0.5,
    logoImageUrl: '',
    logoMediaId: '',
    overlayOpacity: 0.45,
    textColor: '#ffffff',
  }
}

function normalizeBanner(value: unknown): HomeHeroBannerItem | null {
  if (!isRecord(value)) return null
  const id = stringValue(value.id).trim()
  if (!id) return null

  const defaults = createHomeHeroBanner(id)
  return {
    id,
    name: stringValue(value.name, defaults.name).trim() || defaults.name,
    bodyMarkdown: stringValue(value.bodyMarkdown),
    ctaLabel: stringValue(value.ctaLabel),
    ctaUrl: stringValue(value.ctaUrl),
    ctaOpenInNewTab: booleanValue(value.ctaOpenInNewTab),
    backgroundImageUrl: stringValue(value.backgroundImageUrl),
    backgroundMediaId: stringValue(value.backgroundMediaId),
    backgroundFocusX: boundedNumber(value.backgroundFocusX, defaults.backgroundFocusX),
    backgroundFocusY: boundedNumber(value.backgroundFocusY, defaults.backgroundFocusY),
    logoImageUrl: stringValue(value.logoImageUrl),
    logoMediaId: stringValue(value.logoMediaId),
    overlayOpacity: boundedNumber(value.overlayOpacity, defaults.overlayOpacity),
    textColor: stringValue(value.textColor, defaults.textColor) || defaults.textColor,
  }
}

function normalizeRotationInterval(value: unknown): number {
  return Math.round(
    boundedNumber(
      value,
      DEFAULT_HOME_HERO_ROTATION_SECONDS,
      MIN_HOME_HERO_ROTATION_SECONDS,
      MAX_HOME_HERO_ROTATION_SECONDS
    )
  )
}

function normalizeCurrentConfig(value: UnknownRecord): HomeHeroConfig {
  const seenBannerIds = new Set<string>()
  const banners = (Array.isArray(value.banners) ? value.banners : [])
    .map(normalizeBanner)
    .filter((banner): banner is HomeHeroBannerItem => {
      if (!banner || seenBannerIds.has(banner.id)) return false
      seenBannerIds.add(banner.id)
      return true
    })

  const activeBannerIds: string[] = []
  const seenActiveIds = new Set<string>()
  if (Array.isArray(value.activeBannerIds)) {
    for (const candidate of value.activeBannerIds) {
      if (
        typeof candidate === 'string' &&
        seenBannerIds.has(candidate) &&
        !seenActiveIds.has(candidate)
      ) {
        activeBannerIds.push(candidate)
        seenActiveIds.add(candidate)
      }
    }
  }

  return {
    schemaVersion: HOME_HERO_SCHEMA_VERSION,
    banners,
    activeBannerIds,
    rotationIntervalSeconds: normalizeRotationInterval(value.rotationIntervalSeconds),
  }
}

function normalizeLegacyConfig(value: UnknownRecord): HomeHeroConfig {
  const defaults = createHomeHeroBanner(LEGACY_HOME_HERO_BANNER_ID)
  const bodyMarkdown = stringValue(value.bodyMarkdown)
  const banner = normalizeBanner({
    ...defaults,
    ...value,
    id: LEGACY_HOME_HERO_BANNER_ID,
    name: bannerNameFromMarkdown(bodyMarkdown),
  })

  return {
    schemaVersion: HOME_HERO_SCHEMA_VERSION,
    banners: banner ? [banner] : [],
    activeBannerIds: banner && value.enabled === true ? [banner.id] : [],
    rotationIntervalSeconds: DEFAULT_HOME_HERO_ROTATION_SECONDS,
  }
}

export function normalizeHomeHeroConfig(value: unknown): HomeHeroConfig {
  if (!isRecord(value)) {
    return {
      schemaVersion: HOME_HERO_SCHEMA_VERSION,
      banners: [],
      activeBannerIds: [],
      rotationIntervalSeconds: DEFAULT_HOME_HERO_ROTATION_SECONDS,
    }
  }

  if (value.schemaVersion === HOME_HERO_SCHEMA_VERSION || Array.isArray(value.banners)) {
    return normalizeCurrentConfig(value)
  }

  if ('enabled' in value || 'bodyMarkdown' in value) return normalizeLegacyConfig(value)

  return normalizeCurrentConfig(value)
}

export function getActiveHomeHeroBanners(config: HomeHeroConfig): HomeHeroBannerItem[] {
  const bannersById = new Map(config.banners.map((banner) => [banner.id, banner]))
  return config.activeBannerIds.flatMap((id) => {
    const banner = bannersById.get(id)
    return banner ? [banner] : []
  })
}
